import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
  DispatchTrackingDestination,
  DispatchTrackingPerformer,
  DispatchTrackingRoute,
  LocationMetaOrigin
} from "@ots-frontend/ots-data";
import {Alert, Badge, Box, Group, Loader, Paper, Text} from "@mantine/core";
import {IconArrowBackUp, IconMapPin, IconRoute, IconTruckDelivery} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {Map as MapComponent, MAP_DEFAULT_CENTER, useMapLoader} from "../../../map/Map/Map";
import {Marker} from "../../../map/marker/Marker/Marker";
import {
  getTrackingDestinationMarkerIcon,
  getTrackingPerformerMarkerIcon,
  getTrackingSourceMarkerIcon,
  TRACKING_MAP_OPTIONS,
  TRACKING_ROUTE_OPTIONS
} from "../../../map/tracking/tracking-map-style";
import {DispatchTrackingInfoWindow} from "./DispatchTrackingInfoWindow";
import {RouteGeometryPolyline, toPath} from "../../../map/polyline/RoutingPolyline/RoutingPolyline";

export type DispatchTrackingMapProps = {
  performer?: DispatchTrackingPerformer;
  destination?: DispatchTrackingDestination;
  route?: DispatchTrackingRoute;
}

type ActiveInfoWindow =
  | { kind: "performer", position: google.maps.LatLngLiteral }
  | { kind: "destination", position: google.maps.LatLngLiteral }
  | { kind: "source", position: google.maps.LatLngLiteral };

const getPerformerPosition = (performer?: DispatchTrackingPerformer): google.maps.LatLngLiteral | undefined => {
  const location = performer?.locationContext?.location;

  if (location) {
    return {
      lat: location.latitude,
      lng: location.longitude
    };
  }

  return undefined;
}

const getDestinationPosition = (destination?: DispatchTrackingDestination): google.maps.LatLngLiteral | undefined => {
  if (typeof destination?.latitude === "number" && typeof destination.longitude === "number") {
    return {
      lat: destination.latitude,
      lng: destination.longitude
    };
  }

  return undefined;
}

const getOriginPosition = (origin?: LocationMetaOrigin): google.maps.LatLngLiteral | undefined => {
  if (typeof origin?.latitude === "number" && typeof origin.longitude === "number") {
    return {
      lat: origin.latitude,
      lng: origin.longitude
    };
  }

  return undefined;
}

const getDestinationTypeLabel = (
  destination: DispatchTrackingDestination | undefined,
  t: (key: string, defaultValue: string) => string
): string => {
  switch (destination?.type) {
    case "PICKUP":
      return t("common.entity.dispatch.tracking.map.pickup", "Pickup");
    case "DELIVERY":
      return t("common.entity.dispatch.tracking.map.delivery", "Delivery");
    default:
      return t("common.entity.dispatch.tracking.map.destination", "Destination");
  }
}

const getRoutePath = (route?: DispatchTrackingRoute): google.maps.LatLngLiteral[] => {
  return toPath(route?.geometry);
}

const getPositionKey = (position?: google.maps.LatLngLiteral): string => {
  if (!position) {
    return "";
  }

  return `${position.lat.toFixed(6)},${position.lng.toFixed(6)}`;
}

const getDestinationKey = (destination?: DispatchTrackingDestination): string => {
  return [
    destination?.type ?? "",
    destination?.siteId ?? "",
    getPositionKey(getDestinationPosition(destination))
  ].join("|");
}

const getOriginKey = (origin?: LocationMetaOrigin): string => {
  return [
    origin?.source ?? "",
    origin?.siteId ?? "",
    getPositionKey(getOriginPosition(origin))
  ].join("|");
}

const getRouteContextKey = (routePath: google.maps.LatLngLiteral[]): string => {
  if (routePath.length === 0) {
    return "";
  }

  return [
    routePath.length > 1 ? "route" : "point",
    getPositionKey(routePath[routePath.length - 1])
  ].join("|");
}

const getTrackingCenter = (
  performerPosition?: google.maps.LatLngLiteral,
  destinationPosition?: google.maps.LatLngLiteral,
  sourcePosition?: google.maps.LatLngLiteral
): google.maps.LatLngLiteral | undefined => {
  if (!performerPosition) {
    return destinationPosition;
  }

  const contextualPosition = destinationPosition ?? sourcePosition;
  if (!contextualPosition) {
    return performerPosition;
  }

  return {
    lat: (performerPosition.lat + contextualPosition.lat) / 2,
    lng: (performerPosition.lng + contextualPosition.lng) / 2
  };
}

const fitTrackingViewport = (
  map: google.maps.Map,
  points: google.maps.LatLngLiteral[],
  performerPosition?: google.maps.LatLngLiteral,
  destinationPosition?: google.maps.LatLngLiteral
) => {
  if (points.length === 0) {
    return;
  }

  if (points.length === 1) {
    map.setCenter(points[0]);
    map.setZoom(15);
    return;
  }

  const bounds = new google.maps.LatLngBounds();
  points.forEach((point) => bounds.extend(point));

  if (performerPosition && destinationPosition) {
    bounds.extend(performerPosition);
    bounds.extend(destinationPosition);
  }

  map.fitBounds(bounds, 64);

  google.maps.event.addListenerOnce(map, "idle", () => {
    const zoom = map.getZoom();
    if (typeof zoom === "number" && zoom > 16) {
      map.setZoom(16);
    }
  });
}

export const DispatchTrackingMap = (props: DispatchTrackingMapProps) => {
  const {performer, destination, route} = props;
  const {t} = useTranslation(["common"]);
  const mapRef = useRef<google.maps.Map | undefined>(undefined);
  const viewportInitializedRef = useRef(false);
  const lastContextKeyRef = useRef("");
  const lastPerformerPositionKeyRef = useRef("");

  const {isLoaded, loadError} = useMapLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY
  });
  const [activeInfoWindow, setActiveInfoWindow] = useState<ActiveInfoWindow | undefined>(undefined);

  const performerPosition = useMemo(() => getPerformerPosition(performer), [performer]);
  const origin = performer?.locationContext?.meta?.origin;
  const sourcePosition = useMemo(() => getOriginPosition(origin), [origin]);
  const destinationPosition = useMemo(() => getDestinationPosition(destination), [destination]);
  const routePath = useMemo(() => getRoutePath(route), [route]);
  const destinationTypeLabel = useMemo(() => getDestinationTypeLabel(destination, t), [destination, t]);
  const sourceKey = useMemo(() => getOriginKey(origin), [origin]);
  const destinationKey = useMemo(() => getDestinationKey(destination), [destination]);
  const routeContextKey = useMemo(() => getRouteContextKey(routePath), [routePath]);
  const performerPositionKey = useMemo(() => getPositionKey(performerPosition), [performerPosition]);
  const contextKey = useMemo(() => `${sourceKey}|${destinationKey}|${routeContextKey}`, [destinationKey, routeContextKey, sourceKey]);

  const points = useMemo(() => {
    return [
      sourcePosition,
      performerPosition,
      destinationPosition,
      ...routePath
    ].filter((point): point is google.maps.LatLngLiteral => Boolean(point));
  }, [destinationPosition, performerPosition, routePath, sourcePosition]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    fitTrackingViewport(map, points, performerPosition, destinationPosition);
    viewportInitializedRef.current = true;
    lastContextKeyRef.current = contextKey;
    lastPerformerPositionKeyRef.current = performerPositionKey;
  }, [contextKey, destinationPosition, performerPosition, performerPositionKey, points]);

  const onMapUnmount = useCallback(() => {
    mapRef.current = undefined;
    viewportInitializedRef.current = false;
  }, []);

  useEffect(() => {
    const map = mapRef.current;

    if (!map) {
      return;
    }

    if (!viewportInitializedRef.current || lastContextKeyRef.current !== contextKey) {
      fitTrackingViewport(map, points, performerPosition, destinationPosition);
      viewportInitializedRef.current = true;
      lastContextKeyRef.current = contextKey;
      lastPerformerPositionKeyRef.current = performerPositionKey;
      return;
    }

    if (performerPositionKey && lastPerformerPositionKeyRef.current !== performerPositionKey) {
      const center = getTrackingCenter(performerPosition, destinationPosition, sourcePosition);

      if (center) {
        map.panTo(center);
      }

      lastPerformerPositionKeyRef.current = performerPositionKey;
    }
  }, [contextKey, destinationPosition, performerPosition, performerPositionKey, points, sourcePosition]);

  useEffect(() => {
    if (activeInfoWindow?.kind === "performer" && performerPosition) {
      setActiveInfoWindow({kind: "performer", position: performerPosition});
    }
  }, [activeInfoWindow?.kind, performerPosition]);

  const onPerformerMarkerClick = useCallback(() => {
    if (performerPosition) {
      setActiveInfoWindow({kind: "performer", position: performerPosition});
    }
  }, [performerPosition]);

  const onDestinationMarkerClick = useCallback(() => {
    if (destinationPosition) {
      setActiveInfoWindow({kind: "destination", position: destinationPosition});
    }
  }, [destinationPosition]);

  const onSourceMarkerClick = useCallback(() => {
    if (sourcePosition) {
      setActiveInfoWindow({kind: "source", position: sourcePosition});
    }
  }, [sourcePosition]);

  if (points.length === 0) {
    return null;
  }

  if (loadError) {
    return (
      <Alert icon={<IconMapPin size={16}/>} color="yellow">
        {t("common.entity.dispatch.tracking.map.error", "Map could not be loaded.")}
      </Alert>
    );
  }

  if (!isLoaded) {
    return (
      <Box h={360} sx={(theme) => ({
        alignItems: "center",
        backgroundColor: theme.colors.gray[0],
        borderRadius: theme.radius.md,
        display: "flex",
        justifyContent: "center"
      })}>
        <Loader/>
      </Box>
    );
  }

  return (
    <Box h={360} sx={(theme) => ({
      border: `1px solid ${theme.colors.gray[3]}`,
      borderRadius: theme.radius.md,
      overflow: "hidden"
    })}>
      <MapComponent
        onLoad={onMapLoad}
        onUnmount={onMapUnmount}
        center={performerPosition ?? destinationPosition ?? MAP_DEFAULT_CENTER}
        zoom={12}
        mapContainerStyle={{width: "100%", height: "100%"}}
        options={TRACKING_MAP_OPTIONS}
      >
        {routePath.length > 1 &&
          <RouteGeometryPolyline
            id={`dispatch-tracking-route-${route?.etaDttm ?? route?.durationMillis ?? routePath.length}`}
            geometry={route?.geometry}
            {...TRACKING_ROUTE_OPTIONS}/>
        }
        {sourcePosition &&
          <Marker
            position={sourcePosition}
            title={`${t("common.entity.dispatch.tracking.field.source", "Coming From")}${origin?.siteName ? `: ${origin.siteName}` : ""}`}
            icon={getTrackingSourceMarkerIcon()}
            onClick={onSourceMarkerClick}/>
        }
        {destinationPosition &&
          <Marker
            position={destinationPosition}
            title={`${destinationTypeLabel}${destination?.siteName ? `: ${destination.siteName}` : ""}`}
            icon={getTrackingDestinationMarkerIcon()}
            onClick={onDestinationMarkerClick}/>
        }
        {performerPosition &&
          <Marker
            position={performerPosition}
            title={performer?.personName}
            icon={getTrackingPerformerMarkerIcon(performer)}
            onClick={onPerformerMarkerClick}/>
        }
        {activeInfoWindow &&
          <DispatchTrackingInfoWindow
            kind={activeInfoWindow.kind}
            position={activeInfoWindow.position}
            performer={performer}
            destination={destination}
            origin={origin}
            route={route}
            onCloseClick={() => setActiveInfoWindow(undefined)}/>
        }
        <Paper
          p="xs"
          shadow="sm"
          sx={{
            left: 10,
            position: "absolute",
            top: 10
          }}
        >
          <Group spacing="xs">
            <Badge leftSection={<IconTruckDelivery size={12}/>} color="green" variant="light">
              {t("common.entity.dispatch.tracking.map.driver", "Driver")}
            </Badge>
            {sourcePosition &&
              <Badge leftSection={<IconArrowBackUp size={12}/>} color="yellow" variant="light">
                {t("common.entity.dispatch.tracking.field.source", "Coming From")}
              </Badge>
            }
            {destinationPosition &&
              <Badge leftSection={<IconMapPin size={12}/>} color="red" variant="light">
                {destinationTypeLabel}
              </Badge>
            }
            {routePath.length > 1 &&
              <Badge leftSection={<IconRoute size={12}/>} color="yellow" variant="light">
                {t("common.entity.dispatch.tracking.map.route", "Route")}
              </Badge>
            }
          </Group>
          {destination?.siteName &&
            <Text size="xs" color="dimmed" mt={4} maw={260} lineClamp={1}>
              {destinationTypeLabel}: {destination.siteName}
            </Text>
          }
        </Paper>
      </MapComponent>
    </Box>
  );
}
